import { useState } from "react";
import { HandCoins, Check, X, Repeat } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, timeAgo } from "@/lib/api";

const STATUS = {
  pending: ["Waiting for reply", "bg-white/15 text-white"],
  accepted: ["Accepted", "bg-[#d4f253] text-[#0f2a33]"],
  declined: ["Declined", "bg-red-500/80 text-white"],
  countered: ["Countered", "bg-orange-500/80 text-white"],
  withdrawn: ["Replaced by a newer offer", "bg-white/10 text-slate-300"],
};

export function OfferBubble({ m, me, fromSeller, onChange }) {
  const [busy, setBusy] = useState(false);
  const [countering, setCountering] = useState(false);
  const [amount, setAmount] = useState("");
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setCountering(false); onChange(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const reply = (accept) => run(() => api.post(`/offers/${m.id}/respond`, { accept }), accept ? "Accepted — arrange pick-up!" : "Declined");
  const counter = (e) => { e.preventDefault(); run(() => api.post(`/offers/${m.id}/counter`, { amount: Number(amount) }), "Counter-offer sent"); };

  const [label, cls] = STATUS[m.offer_status] || STATUS.pending;
  const kind = fromSeller ? "counter-offer" : "offer";
  const title = me ? `Your ${kind}` : fromSeller ? "Counter-offer from seller" : "Offer received";
  const asking = m.text.match(/\(asking \$[\d,]+\)/)?.[0].slice(1, -1);

  return (
    <div className={`flex ${me ? "justify-end" : "justify-start"}`}>
      <div data-testid={`offer-bubble-${m.id}`} className="w-72 max-w-[85%] rounded-2xl border border-[#d4f253]/40 bg-[#0f2a33] p-4">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#d4f253]"><HandCoins className="h-4 w-4" />{title}</p>
        <p className="mt-1 font-display text-3xl font-extrabold">${m.amount?.toLocaleString()}</p>
        <p className="text-xs text-slate-400">{asking ? `${asking} · ` : ""}paid on pick-up</p>
        <span data-testid={`offer-status-${m.id}`} className={`mt-3 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{label}</span>
        {!me && m.offer_status === "pending" && !countering && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            <button data-testid={`offer-accept-${m.id}`} disabled={busy} onClick={() => reply(true)} className="btn-lime py-2 text-xs"><Check className="h-4 w-4" />Accept</button>
            <button data-testid={`offer-counter-${m.id}`} disabled={busy} onClick={() => setCountering(true)} className="flex items-center justify-center gap-1 rounded-xl border border-[#d4f253]/50 py-2 text-xs font-semibold text-[#d4f253] hover:bg-[#d4f253]/10"><Repeat className="h-3.5 w-3.5" />Counter</button>
            <button data-testid={`offer-decline-${m.id}`} disabled={busy} onClick={() => reply(false)} className="flex items-center justify-center gap-1 rounded-xl border border-white/20 py-2 text-xs font-semibold hover:bg-white/5"><X className="h-4 w-4" />Decline</button>
          </div>
        )}
        {countering && (
          <form onSubmit={counter} className="mt-3 flex gap-2">
            <input data-testid={`offer-counter-input-${m.id}`} type="number" min="1" step="1" required autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Your price $" className="h-9 min-w-0 flex-1 rounded-lg bg-white px-3 text-sm text-[#0f2a33] outline-none" />
            <button data-testid={`offer-counter-send-${m.id}`} disabled={busy || !(Number(amount) > 0)} className="btn-lime px-3 text-xs">Send</button>
            <button type="button" onClick={() => setCountering(false)} className="rounded-lg px-2 text-slate-400 hover:bg-white/5"><X className="h-4 w-4" /></button>
          </form>
        )}
        <p className="mt-2 text-[10px] text-slate-500">{timeAgo(m.created_at)}</p>
      </div>
    </div>
  );
}
